/**
 * Portal Layout
 *
 * Minimal, clean layout wrapper for public portals.
 * Features:
 * - Simple header with branding
 * - Mobile-optimized layout
 * - Security badge footer
 */
import { ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { motion } from 'framer-motion';

interface PortalLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

export function PortalLayout({
  children,
  showHeader = true,
  showFooter = true,
}: PortalLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header - conditionally rendered for form pages */}
      {showHeader && (
        <header className="border-b bg-white">
        <div className="container mx-auto flex h-14 items-center px-4">
          <div className="flex items-center">
            <img src="/logo-dark.svg" alt="Respectly" className="h-8 w-auto" />
          </div>
        </div>
      </header>
      )}

      {/* Main Content */}
      <main className="flex flex-1 flex-col">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1"
        >
          {children}
        </motion.div>
      </main>

      {/* Footer */}
      {showFooter && (
        <footer className="border-t bg-white py-4">
          <div className="container mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Secure & encrypted</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Powered by Respectly
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
